/**
 * Lokala SEO-sidor: copy + metadata för respektive ort.
 * Unik text per ort så sidorna känns som riktiga landningssidor från ett
 * lokalt företag, inte massproducerade SEO-sidor.
 */

const COMMON_FAQ = (city) => [
  {
    question: `Kan ni hjälpa till även om jag inte är på plats i ${city}?`,
    answer:
      "Ja, det går bra. Många av våra kunder är fritidshusägare eller bor inte på adressen där trädet står. Skicka en bild och beskriv läget, så gör vi en första bedömning på distans, återkommer med tydlig offert och planerar arbetet utan att du behöver vara där.",
  },
  {
    question:
      "Kan ni ta hand om både trädfällning, stubbfräsning och bortforsling?",
    answer:
      "Ja, vi kan hjälpa till med hela kedjan i samma uppdrag. Många väljer det för att slippa olika leverantörer och för att tomten ska lämnas ren och direkt användbar efter arbetet.",
  },
  {
    question: (c) => `Vad påverkar priset för trädfällning i ${c}?`,
    answer:
      "Priset beror på trädets storlek, åtkomst, risker, om sektionsfällning eller kranbil krävs samt om du även vill ha stubbfräsning och bortforsling. Skicka gärna en bild på trädet och beskriv platsen så ger vi en första bedömning kostnadsfritt.",
  },
];

export const LOCAL_AREAS = {
  bastad: {
    slug: "tradfallning-bastad",
    city: "Båstad",
    title: "Trädfällning i Båstad | Trädfällarna Laholm AB",
    metaDescription:
      "Säker trädfällning i Båstad — villor, fritidshus och kustnära tomter. Vi hjälper med fällning, stubbfräsning och bortforsling i samma uppdrag.",
    h1: "Trädfällning i Båstad",
    intro:
      "Vi hjälper privatpersoner, fritidshusägare, fastighetsägare och företag i Båstad med säker trädfällning — även där tomten är trång eller utsikten och grannarna måste tas hänsyn till.",
    body: [
      "Båstad är en utpräglad sommar- och fritidsort där många tomter ligger nära havet, på sluttningar eller med träd tätt inpå hus, garage och staket. Det ställer höga krav på rätt metod och försiktig planering — något vi arbetar med dagligen.",
      "Är du sommarboende och inte alltid på plats? Vi gör en bedömning utifrån bilder, skickar en tydlig offert och kan utföra arbetet utan att du behöver vara där. Vi återkopplar med foto efteråt.",
    ],
    bullets: [
      "Trädfällning nära villor och fritidshus",
      "Sektionsfällning för trånga tomter och utsiktslägen",
      "Beskärning och formning av träd",
      "Stubbfräsning och bortforsling i samma uppdrag",
      "Lokal kännedom — vi arbetar i området regelbundet",
    ],
    commonJobsIntro:
      "I Båstad har vi ofta uppdrag där förutsättningarna kräver lugn planering och rätt teknik. Några exempel på vad vi vanligtvis hjälper till med:",
    commonJobs: [
      "Träd nära hus, balkong eller skorsten där sektionsfällning krävs",
      "Träd vid tomtgräns där hänsyn till grannar är viktig",
      "Träd som skymmer havsutsikt eller stör solen på altanen",
      "Fritidshus där ägaren inte är på plats — vi hanterar hela uppdraget",
      "Helhetslösningar med fällning, stubbfräsning och bortforsling",
    ],
    image: "/images/hero-local-areas.webp",
  },

  angelholm: {
    slug: "tradfallning-angelholm",
    city: "Ängelholm",
    title: "Trädfällning i Ängelholm | Trädfällarna Laholm AB",
    metaDescription:
      "Trädfällning, beskärning och stubbfräsning i Ängelholm. Vi hjälper privatpersoner, fastighetsägare och företag i hela kommunen.",
    h1: "Trädfällning i Ängelholm",
    intro:
      "Vi hjälper privatpersoner, fastighetsägare och företag i Ängelholm med trädfällning, beskärning och stubbfräsning — från villaområden till företagstomter och bostadsrättsföreningar.",
    body: [
      "I Ängelholm finns många villaområden med äldre, stora träd som med tiden behöver tas ner eller beskäras för att inte skada hus, ledningar eller tomtgränser. Vi gör alltid en bedömning på plats och rekommenderar den metod som är säkrast — markfällning, sektionsfällning eller kranbil.",
      "Vi tar oss även an återkommande uppdrag för fastighetsägare, bostadsrättsföreningar och företag. Det kan handla om grönyteskötsel, häckklippning eller snöröjning vintertid.",
    ],
    bullets: [
      "Trädfällning i villaområden och på företagstomter",
      "Beskärning av äldre fruktträd och stora lövträd",
      "Stubbfräsning inför ny gräsmatta eller plantering",
      "Bortforsling av ris och grenar",
      "Återkommande grönyteskötsel för fastigheter",
    ],
    commonJobsIntro:
      "I Ängelholm är våra vanligaste uppdrag fördelade mellan privata trädgårdar och fastigheter som behöver löpande hjälp:",
    commonJobs: [
      "Äldre lövträd nära hus, garage eller uppfart",
      "Beskärning av fruktträd och formning av prydnadsträd",
      "Trädfällning vid tomtgräns där det krävs hänsyn till granne",
      "Fastighets- och företagsuppdrag med flera träd samtidigt",
      "Stubbfräsning och bortforsling efter större arbeten",
    ],
    image: "/images/hero-local-areas.webp",
  },

  orkelljunga: {
    slug: "tradfallning-orkelljunga",
    city: "Örkelljunga",
    title: "Trädfällning i Örkelljunga | Trädfällarna Laholm AB",
    metaDescription:
      "Trädfällning i Örkelljunga — större tomter, gårdar och skogsnära fastigheter. Vi hjälper med säker fällning, stubbfräsning och bortforsling.",
    h1: "Trädfällning i Örkelljunga",
    intro:
      "Vi hjälper privatpersoner, gårdsägare och fastighetsägare i Örkelljunga med säker trädfällning — också där tomten är stor, gränsar till skog eller har träd nära byggnader och uppfart.",
    body: [
      "Tomter i Örkelljunga är ofta större och ligger nära skog, vilket innebär att uppdragen kan handla om flera träd, riskbedömning nära byggnader eller behov av kranbil för att hantera virket effektivt.",
      "Vi kombinerar gärna fällning med stubbfräsning och bortforsling så att tomten lämnas redo att användas direkt efteråt — utan kvarvarande stubbar eller högar med ris.",
    ],
    bullets: [
      "Fällning av enstaka träd eller hela grupper",
      "Skogsnära uppdrag på större tomter",
      "Kranbilsfällning vid trånga eller känsliga lägen",
      "Stubbfräsning och bortforsling efteråt",
      "Råd kring vilka träd som bör tas eller sparas",
    ],
    commonJobsIntro:
      "I Örkelljunga är uppdragen ofta något större än i en typisk villatomt. Några återkommande exempel:",
    commonJobs: [
      "Flera träd på samma tomt — ofta i kombination med röjning",
      "Träd nära byggnader, ladugårdar eller uppfarter",
      "Avverkning där virket ska tas omhand på platsen",
      "Skogsnära träd som blivit en risk vid kraftig vind",
      "Helhetsuppdrag med stubbfräsning och bortforsling",
    ],
    image: "/images/hero-local-areas.webp",
  },

  halmstad: {
    slug: "tradfallning-halmstad",
    city: "Halmstad",
    title: "Trädfällning i Halmstad | Trädfällarna Laholm AB",
    metaDescription:
      "Trygg trädfällning i Halmstad. Vi hjälper privatpersoner, fastigheter och företag med fällning, stubbfräsning och beskärning i hela kommunen.",
    h1: "Trädfällning i Halmstad",
    intro:
      "I Halmstad arbetar vi med trädfällning, beskärning och stubbfräsning för privatpersoner, företag och fastighetsägare i hela kommunen — från villaområden till företagstomter.",
    body: [
      "Många träd i Halmstadområdet står nära byggnader, parkeringar, uppfarter eller ledningar. Vi rekommenderar alltid den metod som är säkrast — markfällning där platsen tillåter, annars sektionsfällning eller kranbil för att kontrollera fallet helt.",
      "För företag och fastigheter tar vi gärna helhetsuppdrag: fällning, stubbfräsning och bortforsling i samma arbete, så att du slipper hantera flera leverantörer och får tomten klar direkt.",
    ],
    bullets: [
      "Trädfällning i villa- och radhusområden",
      "Tjänster för företag och fastigheter",
      "Säker hantering nära byggnader och ledningar",
      "Stubbfräsning och bortforsling som tillval",
      "Tydlig offert med RUT-avdrag där det gäller",
    ],
    commonJobsIntro:
      "I Halmstad varierar uppdragen beroende på område, men några exempel återkommer ofta:",
    commonJobs: [
      "Träd nära hus, uppfart eller garage i villaområden",
      "Träd vid tomtgräns där grannar bör informeras",
      "Företagstomter och fastigheter med flera träd samtidigt",
      "Träd nära ledningar eller vägar där säkerheten är avgörande",
      "Helhetsuppdrag med stubbfräsning och bortforsling efteråt",
    ],
    image: "/images/hero-local-areas.webp",
  },
};

// Bygg FAQ per ort
Object.values(LOCAL_AREAS).forEach((area) => {
  area.faq = COMMON_FAQ(area.city).map((item) => ({
    question:
      typeof item.question === "function" ? item.question(area.city) : item.question,
    answer:
      typeof item.answer === "function" ? item.answer(area.city) : item.answer,
  }));
});

export const LOCAL_AREAS_LIST = Object.values(LOCAL_AREAS);
